import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Actions,
    Box,
    Button,
    Column,
    Columns,
    IconAdd,
    IconEdit,
    Inline,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Text,
    TextField,
    TooltipRenderer,
} from "braid-design-system";
import { GLAccountResponse } from "../../services/glAccountService";

interface GLAccountListTableProps {
    glAccountList?: Array<GLAccountResponse> | null;
    onCreateGLAccount?: () => void;
    onEditGLAccount?: (account: GLAccountResponse) => void;
}

const getActiveLabel = (status: number | boolean | null | undefined): string => {
    if (typeof status === "boolean") {
        return status ? "Active" : "Inactive";
    }

    return Number(status ?? 0) === 1 ? "Active" : "Inactive";
};

const GLAccountListTable: React.FC<GLAccountListTableProps> = ({
    glAccountList,
    onCreateGLAccount,
    onEditGLAccount,
}) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState("");

    const filteredAccounts = useMemo(() => {
        if (!glAccountList) {
            return glAccountList;
        }

        const normalizedSearch = searchTerm.trim().toLowerCase();

        return glAccountList.filter((account) => {
            if (!normalizedSearch) {
                return true;
            }

            return (
                account.acc_code?.toLowerCase().includes(normalizedSearch) ||
                account.acc_name?.toLowerCase().includes(normalizedSearch) ||
                account.acc_branch_id?.toLowerCase().includes(normalizedSearch)
            );
        });
    }, [glAccountList, searchTerm]);

    const showActions = typeof onEditGLAccount === "function";

    return (
        <Stack space="large">
            <Columns space="large" collapseBelow="tablet">
                <Column width="1/3">
                    <Box display="flex" justifyContent="flexStart">
                        {onCreateGLAccount ? (
                            <Actions>
                                <Button
                                    variant="ghost"
                                    tone="brandAccent"
                                    icon={<IconAdd />}
                                    onClick={onCreateGLAccount}
                                >
                                    {t("page.gl_account_management.list.button.create_new", {
                                        defaultValue: "Create New GL Account",
                                    })}
                                </Button>
                            </Actions>
                        ) : null}
                    </Box>
                </Column>
                <Column width="2/3">
                    <Box display="flex" justifyContent="flexEnd" alignItems="center">
                        <TextField
                            placeholder={t("page.gl_account_management.list.search.placeholder", {
                                defaultValue: "Search GL Accounts...",
                            })}
                            aria-label={t("page.gl_account_management.list.search.aria", {
                                defaultValue: "Search GL Accounts",
                            })}
                            onChange={(e) => setSearchTerm(e.currentTarget.value)}
                            value={searchTerm}
                        />
                    </Box>
                </Column>
            </Columns>

            <Table label={t("page.gl_account_management.list.table.label", { defaultValue: "GL Accounts List Table" })}>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell><Text>{t("page.gl_account_management.list.table.headers.code", { defaultValue: "Code" })}</Text></TableHeaderCell>
                        <TableHeaderCell><Text>{t("page.gl_account_management.list.table.headers.name", { defaultValue: "Name" })}</Text></TableHeaderCell>
                        <TableHeaderCell><Text>{t("page.gl_account_management.list.table.headers.branch", { defaultValue: "Branch" })}</Text></TableHeaderCell>
                        <TableHeaderCell><Text>{t("page.gl_account_management.list.table.headers.active", { defaultValue: "Active" })}</Text></TableHeaderCell>
                        {showActions ? <TableHeaderCell align="right"><Text>{t("page.gl_account_management.list.table.headers.actions", { defaultValue: "Actions" })}</Text></TableHeaderCell> : null}
                    </TableRow>
                </TableHeader>

                <TableBody>
                    {filteredAccounts && filteredAccounts.length > 0 ? (
                        filteredAccounts.map((account) => (
                            <TableRow key={account.id ?? account.acc_code ?? "gl-account-row"}>
                                <TableCell><Text>{account.acc_code || t("page.gl_account_management.list.table.na", { defaultValue: "N/A" })}</Text></TableCell>
                                <TableCell><Text>{account.acc_name || t("page.gl_account_management.list.table.na", { defaultValue: "N/A" })}</Text></TableCell>
                                <TableCell><Text>{account.acc_branch_id || t("page.gl_account_management.list.table.na", { defaultValue: "N/A" })}</Text></TableCell>
                                <TableCell><Text>{getActiveLabel(account.status)}</Text></TableCell>
                                {showActions ? (
                                    <TableCell align="right">
                                        <Inline space="small">
                                            <TooltipRenderer tooltip={<Text>{t("page.gl_account_management.list.tooltip.edit", { defaultValue: "Edit GL Account" })}</Text>}>
                                                {({ triggerProps }) => (
                                                    <Box
                                                        onClick={() => onEditGLAccount && onEditGLAccount(account)}
                                                        cursor="pointer"
                                                        {...triggerProps}
                                                    >
                                                        <IconEdit tone="info" />
                                                    </Box>
                                                )}
                                            </TooltipRenderer>
                                        </Inline>
                                    </TableCell>
                                ) : null}
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colspan={showActions ? 5 : 4} align="center">
                                <Text>{t("page.gl_account_management.list.empty", { defaultValue: "No GL accounts found." })}</Text>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Stack>
    );
};

export default GLAccountListTable;